// Marketplace seed catalog.
// Kinds: 'library' | 'template' | 'plugin' | 'registrar'
// Note: 'integration' entries have been removed — they are now served
// exclusively from the live `integration_kinds` Supabase table (migration 0063).
// At runtime `hydrateLiveCatalog` (functions/index.js) merges live rows on top
// of this static list, keyed by integration id.
//
// Price 0 = free.
// `thumbnail` — 16:9 preview URL (used by libraries/templates as card/detail
//               hero). Empty → the card renders a transparent-checker
//               placeholder until a real thumbnail is uploaded.
//
// `description` — a POLYGLOT KEY (`apps.catalog.<id>.descr`), not copy. This
// file is DATA rendered by six different sites in `packages/marketplace`, so
// it takes the key-in-state form of docs/I18N-CONVENTION.md §1: the row
// carries the key, and every render site resolves it with
// `el.call('polyglot', item.description)`. `translate()` returns its input
// unchanged when nothing resolves, so a LIVE row merged on top by
// `hydrateLiveCatalog` — whose description comes from the
// `integration_kinds` table and is real prose, not a key — passes through
// untouched.
//
// `name` is SPLIT, and the split is the ruling, not a convenience. 13 rows have
// a DESCRIPTIVE name — `Code Review`, `Docs Writer`, `Accessibility Audit` —
// and those carry `apps.catalog.<id>.name`, so the App Store reads Georgian for
// a Georgian reader (Nika, 2026-09-03: "also translate all appstore apps").
// The other 18 stay literals because translating them would be wrong, not
// merely unnecessary: 9 registrar BRANDS (`Namecheap`), three PRODUCT names
// (`Symbols MCP`, `i18n Polyglot`, `Monaco Editor`) and 6 SLASH COMMANDS
// (`/review`) a user types verbatim — a translated command does not run.
// No render site branches on which is which: `storeItemText` passes a
// non-key through `translate()` unchanged, so both forms take one path.
//
// `name` is also the PRIMARY search field, so `_searchName` in
// `packages/marketplace/functions/index.js` resolves it through the same
// English base `catalogSearchText` uses for the description — a query matches
// the English words, and no comparator ever sorts on a dotted key.

// The catalog's `description` is a bundle KEY (see the note above). The
// marketplace's store search (`_storeSearchTexts` in
// `packages/marketplace/functions/index.js`, matched by `catalogSearch.js`)
// searches the words the member reads through `storeItemText` when it holds an
// element, AND always this English base — a bare call holds no element and
// has only the base. So it resolves a key through the BUNDLED English base
// here, never through `polyglot`: search never degrades to matching dotted
// keys.
//
// Written against `globalThis.__i18nRegistry` directly, with the merge inlined
// and memoized ON THE REGISTRY OBJECT — no module-scope `let` and no bare
// sibling call, both of which frank's serializer cannot revive
// (`__tests__/frankModuleMutableGate.test.js`). `reg.base` is the STATIC
// bundle map, so the memo is keyed on its namespace count and rebuilds if a
// namespace is ever added at runtime.
//
// A value that is not a key — every live `integration_kinds` row, every
// hydrated template — misses the map and comes back untouched.
export const catalogSearchText = (value) => {
  if (!value) return ''
  const w = typeof window !== 'undefined' ? window : globalThis
  const reg = w.__i18nRegistry || globalThis.__i18nRegistry
  if (!reg || !reg.base) return value
  const names = Object.keys(reg.base)
  if (!reg.__enMerged || reg.__enMergedCount !== names.length) {
    const merged = {}
    for (const ns of names) Object.assign(merged, reg.base[ns])
    reg.__enMerged = merged
    reg.__enMergedCount = names.length
  }
  return reg.__enMerged[value] || value
}

export const marketplaceCatalog = [
  // ─── Libraries ────────────────────────────────────────────────────────────
  // Library entries removed for the same reason templates were: the four
  // static stubs (Brand / Shared / CMS UI Kit / Charts Kit) didn't back
  // real installable libraries — clicking Install on them did nothing
  // meaningful and the cards rendered with no real thumbnail. Libraries
  // should be sourced from the live SDK the same way templates already
  // are (see hydrateTemplatesFromSdk in marketplace/functions/index.js).
  // Until that hydrator lands for kind=library, the section is empty
  // rather than full of misleading placeholder cards.

  // ─── Templates ────────────────────────────────────────────────────────────
  // Template entries are now sourced exclusively from the SDK at runtime via
  // `hydrateTemplatesFromSdk` (marketplace/functions/index.js) — it queries
  // /projects/public?projectType=template and merges each row into the
  // catalog as kind=template with a screenshot.symbo.ls cover. The previous
  // static stubs (saas-starter / landing / docs / ecommerce) didn't back
  // real projects, so install/use clicks 404'd. Removed.

  // ─── Domain Registrars ───────────────────────────────────────────────────
  {
    id: 'reg.namecheap',
    kind: 'registrar',
    category: 'domains',
    name: 'Namecheap',
    description: 'apps.catalog.regNamecheap.descr',
    price: 0,
    logo: 'namecheap'
  },
  {
    id: 'reg.godaddy',
    kind: 'registrar',
    category: 'domains',
    name: 'GoDaddy',
    description: 'apps.catalog.regGodaddy.descr',
    price: 0,
    logo: 'godaddy'
  },
  {
    id: 'reg.cloudflare-registrar',
    kind: 'registrar',
    category: 'domains',
    name: 'Cloudflare Registrar',
    description: 'apps.catalog.regCloudflareRegistrar.descr',
    price: 0,
    logo: 'cloudflare'
  },
  {
    id: 'reg.porkbun',
    kind: 'registrar',
    category: 'domains',
    name: 'Porkbun',
    description: 'apps.catalog.regPorkbun.descr',
    price: 0,
    logo: 'porkbun'
  },
  {
    id: 'reg.gandi',
    kind: 'registrar',
    category: 'domains',
    name: 'Gandi',
    description: 'apps.catalog.regGandi.descr',
    price: 0,
    logo: 'gandi'
  },
  {
    id: 'reg.squarespace-domains',
    kind: 'registrar',
    category: 'domains',
    name: 'Squarespace Domains',
    description: 'apps.catalog.regSquarespaceDomains.descr',
    price: 0,
    logo: 'squarespace'
  },
  {
    id: 'reg.namesilo',
    kind: 'registrar',
    category: 'domains',
    name: 'NameSilo',
    description: 'apps.catalog.regNamesilo.descr',
    price: 0
  },
  {
    id: 'reg.hover',
    kind: 'registrar',
    category: 'domains',
    name: 'Hover',
    description: 'apps.catalog.regHover.descr',
    price: 0
  },
  {
    id: 'reg.name',
    kind: 'registrar',
    category: 'domains',
    name: 'Name.com',
    description: 'apps.catalog.regName.descr',
    price: 0
  },

  // ─── MCP Connectors ───────────────────────────────────────────────────────
  // MCP (Model Context Protocol) servers that canvas AI agents can call into.
  // Separate from integrations — different runtime contract (JSON-RPC, tools/list, resources/list).
  {
    id: 'mcp.symbols',
    kind: 'mcp',
    category: 'system',
    name: 'Symbols MCP',
    description: 'apps.catalog.mcpSymbols.descr',
    price: 0,
    url: 'https://mcp.symbols.app',
    transport: 'sse',
    auth_kind: 'oauth'
  },

  // ─── Plugins ─────────────────────────────────────────────────────────────
  {
    id: 'plg.seo',
    kind: 'plugin',
    category: 'marketing',
    name: 'apps.catalog.plgSeo.name',
    description: 'apps.catalog.plgSeo.descr',
    price: 0
  },
  {
    id: 'plg.i18n',
    kind: 'plugin',
    category: 'content',
    name: 'i18n Polyglot',
    description: 'apps.catalog.plgI18n.descr',
    price: 0
  },
  {
    id: 'plg.a11y',
    kind: 'plugin',
    category: 'quality',
    name: 'apps.catalog.plgA11y.name',
    description: 'apps.catalog.plgA11y.descr',
    price: 0
  },
  {
    id: 'plg.gridstack',
    kind: 'plugin',
    category: 'editor',
    name: 'apps.catalog.plgGridstack.name',
    description: 'apps.catalog.plgGridstack.descr',
    price: 0
  },
  {
    id: 'plg.monaco',
    kind: 'plugin',
    category: 'editor',
    name: 'Monaco Editor',
    description: 'apps.catalog.plgMonaco.descr',
    price: 0
  },

  // ─── Prompts ─────────────────────────────────────────────────────────────
  // Reusable prompt templates — drop into a new chat thread or pass to an
  // agent. `body` carries the raw prompt; the modal copy button copies
  // that to clipboard. `tokenEstimate` is rough Claude tokens for the
  // prompt itself (response tokens depend on the run).
  {
    id: 'prm.code-review',
    kind: 'prompt',
    category: 'coding',
    name: 'apps.catalog.prmCodeReview.name',
    description: 'apps.catalog.prmCodeReview.descr',
    price: 0,
    body: 'Review the following diff line-by-line. Flag: (1) correctness bugs, (2) edge cases, (3) security risks, (4) style mismatches with surrounding code, (5) missing or misleading tests. Do not paraphrase the diff back. Output a numbered punch list.',
    model: 'claude-opus-4-7',
    tokenEstimate: 80,
    useCount: 0
  },
  {
    id: 'prm.bug-rca',
    kind: 'prompt',
    category: 'coding',
    name: 'apps.catalog.prmBugRca.name',
    description: 'apps.catalog.prmBugRca.descr',
    price: 0,
    body: 'Given the bug report below, identify the root cause. Steps: (1) reproduce locally if possible, (2) walk the call chain, (3) state the invariant that broke, (4) propose the smallest fix, (5) write a regression test. Show your work.',
    model: 'claude-sonnet-4-6',
    tokenEstimate: 70,
    useCount: 0
  },
  {
    id: 'prm.api-design',
    kind: 'prompt',
    category: 'coding',
    name: 'apps.catalog.prmApiDesign.name',
    description: 'apps.catalog.prmApiDesign.descr',
    price: 0,
    body: 'Critique this API surface from three angles: (1) consistency with existing endpoints, (2) ease of use for first-time integrators, (3) backwards-compat risk if we ship and need to evolve. Suggest concrete changes.',
    model: 'claude-opus-4-7',
    tokenEstimate: 90,
    useCount: 0
  },
  {
    id: 'prm.prd-draft',
    kind: 'prompt',
    category: 'product',
    name: 'apps.catalog.prmPrdDraft.name',
    description: 'apps.catalog.prmPrdDraft.descr',
    price: 0,
    body: 'Turn the notes below into a one-page PRD with sections: Problem, Users, Goals, Non-goals, Solution sketch, Open questions, Risks. Aim for ~300 words. Cite the notes when claims rely on them.',
    model: 'claude-opus-4-7',
    tokenEstimate: 60,
    useCount: 0
  },
  {
    id: 'prm.commit-msg',
    kind: 'prompt',
    category: 'coding',
    name: 'apps.catalog.prmCommitMsg.name',
    description: 'apps.catalog.prmCommitMsg.descr',
    price: 0,
    body: 'Read the staged diff and write a commit message: (1) imperative subject under 72 chars, (2) blank line, (3) body explaining WHY (not WHAT), (4) bullet points only if diff spans multiple concerns. Skip if the change is trivial.',
    model: 'claude-haiku-4-5-20251001',
    tokenEstimate: 50,
    useCount: 0
  },

  // ─── Agents ──────────────────────────────────────────────────────────────
  // Claude Code subagents — drop into ~/.claude/agents/<key>.md. Each
  // carries `tools[]` (allow-list), `model` (default), and `body` (the
  // agent's system prompt). Modal renders the body markdown plus a
  // copy-install snippet for the local CLI.
  {
    id: 'agt.code-reviewer',
    kind: 'agent',
    category: 'coding',
    name: 'apps.catalog.agtCodeReviewer.name',
    description: 'apps.catalog.agtCodeReviewer.descr',
    price: 0,
    model: 'claude-opus-4-7',
    tools: ['Read', 'Bash', 'Grep'],
    body: 'You are an independent code reviewer. Read the diff in the conversation context, walk it line-by-line, and report: correctness bugs, edge cases, missing tests, and style mismatches. Do not edit files.',
    useCount: 0
  },
  {
    id: 'agt.security-auditor',
    kind: 'agent',
    category: 'security',
    name: 'apps.catalog.agtSecurityAuditor.name',
    description: 'apps.catalog.agtSecurityAuditor.descr',
    price: 0,
    model: 'claude-opus-4-7',
    tools: ['Read', 'Grep', 'WebSearch'],
    body: 'You are a security reviewer. For the diff in scope, look for: SQL injection, XSS, CSRF, insecure direct object references, missing authz checks, secret leakage, broken crypto, outdated dependencies. Output severity (P0/P1/P2) per finding.',
    useCount: 0
  },
  {
    id: 'agt.test-writer',
    kind: 'agent',
    category: 'coding',
    name: 'apps.catalog.agtTestWriter.name',
    description: 'apps.catalog.agtTestWriter.descr',
    price: 0,
    model: 'claude-sonnet-4-6',
    tools: ['Read', 'Edit', 'Bash'],
    body: "You write tests in the project's existing test framework. For a given feature: (1) write a failing test, (2) run it to confirm red, (3) only then implement until green, (4) refactor if needed. Stop after each step and report.",
    useCount: 0
  },
  {
    id: 'agt.docs-writer',
    kind: 'agent',
    category: 'docs',
    name: 'apps.catalog.agtDocsWriter.name',
    description: 'apps.catalog.agtDocsWriter.descr',
    price: 0,
    model: 'claude-sonnet-4-6',
    tools: ['Read', 'Grep'],
    body: 'You write developer docs. Read the source first, then draft: (1) what the module does, (2) public API with examples, (3) common gotchas. Cite file:line for every claim. Refuse to invent behaviour you cannot verify.',
    useCount: 0
  },
  {
    id: 'agt.refactor',
    kind: 'agent',
    category: 'coding',
    name: 'apps.catalog.agtRefactor.name',
    description: 'apps.catalog.agtRefactor.descr',
    price: 0,
    model: 'claude-opus-4-7',
    tools: ['Read', 'Grep', 'Edit', 'Bash'],
    body: 'You plan and execute refactors. Always: (1) survey all callers first, (2) propose the rename/extract plan, (3) wait for confirmation, (4) apply changes file-by-file with tests run between each. Never bulk-edit without a plan.',
    useCount: 0
  },

  // ─── Skills (slash commands) ─────────────────────────────────────────────
  // Claude Code skills installed at ~/.claude/skills/<key>/SKILL.md.
  // Each carries a `trigger` (when to surface it) + the body markdown.
  // The modal "Install" button copies the body to clipboard with the
  // path it should land at — local install is one paste away.
  {
    id: 'skl.security-review',
    kind: 'skill',
    category: 'quality',
    name: '/security-review',
    description: 'apps.catalog.sklSecurityReview.descr',
    price: 0,
    trigger: '`/security-review`',
    useCount: 0,
    body: `# /security-review

Run a security pass over pending changes — staged, unstaged, or the diff against \`origin/main\`. Hunts for the OWASP Top 10 plus auth/authz gaps, secret exposure, and broken crypto.

## When to use

- Before opening a PR that touches request handling, auth flows, or anything reading user input
- After accepting AI-generated changes that touch security-sensitive paths
- During code review when you want a second opinion on a specific diff

## What it checks

- **Injection**: SQL, NoSQL, command, header injection
- **Auth**: missing authn checks, broken authz (IDOR), session fixation
- **Crypto**: weak hashing (MD5/SHA1), hardcoded keys, ECB mode, predictable IVs
- **Secrets**: tokens / keys / passwords in code or logs
- **Input validation**: untrusted data crossing trust boundaries
- **Dependencies**: known-vulnerable versions in lockfile

## Output

Severity-ranked findings (P0/P1/P2) with file:line references and a suggested fix per finding. Skips findings that exist on \`main\` already — only flags what your diff introduces.
`
  },
  {
    id: 'skl.review',
    kind: 'skill',
    category: 'quality',
    name: '/review',
    description: 'apps.catalog.sklReview.descr',
    price: 0,
    trigger: '`/review [PR#]`',
    useCount: 0,
    body: `# /review

End-to-end review of a PR or local branch. Reads the diff line-by-line and reports correctness, style, missing tests, and architectural smells.

## Usage

- \`/review\` — review the current branch against \`main\`
- \`/review 1234\` — review GitHub PR #1234 (uses \`gh pr view\`)
- \`/review HEAD~3..HEAD\` — review an arbitrary commit range

## What it does

1. Pulls the diff
2. Walks each file change in context
3. Cross-references callers for any modified function (catches breaking changes)
4. Emits a punch list grouped by severity

## What it skips

- Whitespace-only changes
- Generated files / lockfiles
- Test fixture data
`
  },
  {
    id: 'skl.init',
    kind: 'skill',
    category: 'system',
    name: '/init',
    description: 'apps.catalog.sklInit.descr',
    price: 0,
    trigger: '`/init`',
    useCount: 0,
    body: `# /init

Bootstraps a \`CLAUDE.md\` at the repo root by reading the codebase.

## When to run

- First time cloning a repo into a Claude Code session
- After significant refactor — the existing CLAUDE.md is stale

## What it captures

- Build / test / lint commands (from package.json, Makefile, etc.)
- Project layout (top-level dirs + their purpose)
- Framework conventions in use
- Anything else worth pinning so future sessions don't re-derive it

## Output

A \`CLAUDE.md\` you can review and commit. It's deliberately concise — the goal is to prime the next session, not document the codebase.
`
  },
  {
    id: 'skl.simplify',
    kind: 'skill',
    category: 'coding',
    name: '/simplify',
    description: 'apps.catalog.sklSimplify.descr',
    price: 0,
    trigger: '`/simplify`',
    useCount: 0,
    body: `# /simplify

Audits pending changes for over-engineering. Flags premature abstractions, dead code, unnecessary defensive checks, and feature flags that aren't doing anything.

## What it looks for

- Abstractions with one caller (delete the abstraction)
- Try/catch that swallows errors no one handles
- Configuration knobs that no one toggles
- "Just in case" null checks where the type system guarantees non-null
- Helper functions that are thinner than their inline equivalent

## Output

A list of suggested deletions / inlining opportunities, ordered by code reduction.
`
  },
  {
    id: 'skl.loop',
    kind: 'skill',
    category: 'automation',
    name: '/loop',
    description: 'apps.catalog.sklLoop.descr',
    price: 0,
    trigger: '`/loop 5m /<task>`',
    useCount: 0,
    body: `# /loop

Re-runs a prompt on a recurring schedule.

## Usage

- \`/loop 5m /tests\` — run \`/tests\` every 5 minutes
- \`/loop 1h /security-review\` — hourly security pass
- \`/loop dynamic <prompt>\` — let Claude decide the cadence

## When to use

- Long-running tests / builds — poll until green
- Watching for an external event (webhook, queue depth)
- Iterating on a prompt where each run informs the next

## Stopping

\`/loop stop\` cancels the running schedule. Or just close the session — schedules don't persist across sessions unless you use \`/schedule\` for remote runs.
`
  },
  {
    id: 'skl.schedule',
    kind: 'skill',
    category: 'automation',
    name: '/schedule',
    description: 'apps.catalog.sklSchedule.descr',
    price: 0,
    trigger: '`/schedule …`',
    useCount: 0,
    body: `# /schedule

Schedules a remote agent run on a cron schedule. Unlike \`/loop\` (which polls within the current session), \`/schedule\` runs in the cloud — you can close your laptop.

## Usage

- \`/schedule daily 9am /weekly-review\` — run \`/weekly-review\` every weekday at 9am
- \`/schedule every-10-min /watch-deploys\` — frequent polling
- \`/schedule cron "0 */4 * * *" /<prompt>\` — raw cron expression

## When to use

- Anything you want to run while you're not at the keyboard
- Periodic reports / digests
- Long-running watches (deployments, CI status, queue health)

## Where output lands

Each scheduled run creates a session you can review later. Failures email you. Successes are silent unless you wire up notifications.
`
  }
]
